<script setup lang="ts">
import WidgetQuizHeader from './WidgetQuizHeader.vue'
import WidgetQuizStart from './WidgetQuizStart.vue'
import WidgetQuizQuestion from './WidgetQuizQuestion.vue'
import WidgetQuizResults from './WidgetQuizResults.vue'
import type { TQuizScreen, IQuiz, IUserAnswer, IAttemptResult, IAnswerReveal } from './types'
import { useQuiz } from '~/composables/useQuiz'
import { useUser } from '~/composables/useUser'
import { useAuthModalStore } from '~/stores/authModal'
import { useToast } from '~/components/ui/toast'

const props = defineProps<{ quiz: IQuiz }>()
const emit = defineEmits<{ done: [] }>()

const { isLoggedIn } = useUser()
const authModalStore = useAuthModalStore()
const { toast } = useToast()
const { t } = useI18n()

const { quiz: quizState, questions, checkAttempt, startAttempt, fetchQuestion, submitAnswer, getResults, claimGuestAttempts, resetAttempt } = useQuiz(props.quiz.id)

const screen = ref<TQuizScreen>('start')
const result = ref<IAttemptResult | null>(null)
const isSubmitting = ref(false)
const isInitializing = ref(true)
const hasActiveAttempt = ref(false)
// Correct answer and explanation of the question just answered; the question is then read-only
const reveal = ref<IAnswerReveal | null>(null)
const secondsLeft = ref<number | null>(null)
let timerInterval: ReturnType<typeof setInterval> | null = null

const startTimer = (seconds: number | null) => {
  if (!seconds) return
  if (timerInterval) clearInterval(timerInterval)
  secondsLeft.value = seconds
  timerInterval = setInterval(() => {
    if (secondsLeft.value === null || secondsLeft.value <= 0) {
      stopTimer()
      finishQuiz()
      return
    }
    secondsLeft.value--
  }, 1000)
}

const stopTimer = () => {
  if (timerInterval) clearInterval(timerInterval)
}

const finishQuiz = async () => {
  stopTimer()
  if (!isLoggedIn.value) {
    screen.value = 'auth-gate'
    return
  }
  try {
    result.value = await getResults()
    screen.value = 'results'
  } catch {
    showGenericError()
    // Never leave the user on a screen with no way forward
    screen.value = 'start'
  }
}

onMounted(async () => {
  try {
    hasActiveAttempt.value = await checkAttempt()
  } finally {
    isInitializing.value = false
  }
})

onUnmounted(stopTimer)

const { trackEvent } = useAnalytics()

watch(screen, (current) => {
  if (current === 'results') trackEvent('quiz_complete', { quiz_id: props.quiz.id })
})

watch(isLoggedIn, async (loggedIn) => {
  if (loggedIn && screen.value === 'auth-gate') {
    await claimGuestAttempts()
    result.value = await getResults()
    screen.value = 'results'
  }
})

const beginQuiz = async () => {
  await fetchQuestion()

  // Attempt has no questions left to answer — go straight to results
  if (!questions.value.length) {
    await finishQuiz()
    return
  }

  startTimer(quizState.value?.time_remains_seconds ?? null)
  screen.value = 'question'
}

const showGenericError = () => {
  toast({
    title: t('error.title'),
    description: t('widget.quiz.error.results_failed'),
    variant: 'destructive'
  })
}

const handleStart = async () => {
  try {
    if (!hasActiveAttempt.value) await startAttempt()
    await beginQuiz()
  } catch {
    showGenericError()
  }
}

const handleRestart = async () => {
  try {
    await startAttempt()
    await beginQuiz()
  } catch {
    showGenericError()
  }
}

const currentQuestion = computed(() => questions.value[0] ?? null)
const answeredCount = computed(() => currentQuestion.value ? currentQuestion.value.sort_order - 1 : 0)

// The question screen without a question is a dead end: nothing renders and the
// user has no way out. Any state that lands there is finished instead.
watch([screen, currentQuestion], ([currentScreen, question]) => {
  if (currentScreen === 'question' && !question) finishQuiz()
})

const handleTimeExpired = () => {
  stopTimer()
  screen.value = 'time-expired'
}

const isAlreadyAnswered = (err: unknown) =>
  (err as { statusCode?: number })?.statusCode === 409 || (err as { status?: number })?.status === 409

watch(() => currentQuestion.value?.id, () => {
  reveal.value = null
})

const goToNextQuestion = async () => {
  const nextId = quizState.value?.next_question_id ?? undefined
  await fetchQuestion(nextId)
  reveal.value = null
  if (!questions.value.length) {
    await finishQuiz()
  } else {
    startTimer(quizState.value?.time_remains_seconds ?? null)
  }
}

const isTimeExpiredError = (err: unknown) => {
  const msg = (err as { data?: { message?: string } })?.data?.message
    ?? (err as { message?: string })?.message
    ?? ''
  return msg === 'Time limit for this quiz has expired'
}

const handleAnswer = async (answer: IUserAnswer) => {
  if (isSubmitting.value) return
  isSubmitting.value = true
  stopTimer()
  const secondsBeforeAnswer = secondsLeft.value
  try {
    const answerReveal = await submitAnswer(answer)
    if (answerReveal) {
      // Stay on the question to show the correct answer; "Next" moves on
      reveal.value = answerReveal
      startTimer(secondsBeforeAnswer)
    } else {
      await goToNextQuestion()
    }
  } catch (err) {
    if (isTimeExpiredError(err)) {
      handleTimeExpired()
    } else if (isAlreadyAnswered(err) && currentQuestion.value) {
      // Answered earlier (e.g. in another tab): reload it to show it read-only
      await fetchQuestion(currentQuestion.value.id)
      startTimer(quizState.value?.time_remains_seconds ?? null)
    } else {
      showGenericError()
      startTimer(quizState.value?.time_remains_seconds ?? null)
    }
  } finally {
    isSubmitting.value = false
  }
}

const handleNext = async () => {
  if (isSubmitting.value) return
  isSubmitting.value = true
  stopTimer()
  try {
    await goToNextQuestion()
  } catch (err) {
    if (isTimeExpiredError(err)) {
      handleTimeExpired()
    } else {
      showGenericError()
      startTimer(quizState.value?.time_remains_seconds ?? null)
    }
  } finally {
    isSubmitting.value = false
  }
}

const handleSkip = () => {
  if (!currentQuestion.value) return
  handleAnswer({ question_id: currentQuestion.value.id, answer_id: undefined, text_answer: undefined })
}

const handleBack = async () => {
  if (isSubmitting.value) return
  isSubmitting.value = true
  stopTimer()
  try {
    const prevId = quizState.value?.prev_question_id ?? undefined
    await fetchQuestion(prevId)
    startTimer(quizState.value?.time_remains_seconds ?? null)
  } catch (err) {
    if (isTimeExpiredError(err)) {
      handleTimeExpired()
    } else {
      showGenericError()
      startTimer(quizState.value?.time_remains_seconds ?? null)
    }
  } finally {
    isSubmitting.value = false
  }
}

const handleRetry = () => {
  if (isInitializing.value) return
  stopTimer()
  secondsLeft.value = null
  resetAttempt()
  result.value = null
  hasActiveAttempt.value = false
  screen.value = 'start'
}
</script>

<template>
  <div class="space-y-5">
    <WidgetQuizHeader :quiz="quiz" />

    <WidgetQuizStart
      v-if="screen === 'start'"
      :quiz="quiz"
      :is-loading="isInitializing"
      :has-active-attempt="hasActiveAttempt"
      @start="handleStart"
      @restart="handleRestart"
      @resume="beginQuiz"
    />

    <template v-else>
      <WidgetQuizQuestion
        v-if="screen === 'question' && currentQuestion"
        :key="currentQuestion.id"
        :question="currentQuestion"
        :current-index="answeredCount"
        :total-count="quiz.total_questions_count"
        :seconds-left="secondsLeft"
        :is-submitting="isSubmitting"
        :prefilled-answer="currentQuestion.user_answered"
        :reveal="reveal"
        :can-go-back="!!quizState?.prev_question_id"
        @submit="handleAnswer"
        @skip="handleSkip"
        @next="handleNext"
        @back="handleBack"
      />

      <div
        v-else-if="screen === 'auth-gate'"
        class="overflow-hidden rounded-lg bg-white p-8 text-center"
      >
        <div class="mx-auto mb-5 flex h-20 w-20 items-center justify-center rounded-full bg-blue-50 text-5xl">
          🎉
        </div>
        <h3 class="mb-1 text-xl font-bold text-gray-900">{{ $t('widget.quiz.auth_gate.title') }}</h3>
        <p class="mb-1 text-sm font-medium text-blue-600">{{ $t('widget.quiz.auth_gate.subtitle') }}</p>
        <p class="mb-7 text-sm text-gray-500">{{ $t('widget.quiz.auth_gate.description') }}</p>
        <div class="flex flex-col gap-2">
          <Button variant="blue" class="w-full" @click="authModalStore.openLoginModal()">
            {{ $t('widget.quiz.auth_gate.sign_in') }}
          </Button>
          <Button variant="outline-blue" class="w-full" @click="authModalStore.openRegisterModal()">
            {{ $t('widget.quiz.auth_gate.register') }}
          </Button>
        </div>
      </div>

      <div
        v-else-if="screen === 'time-expired'"
        class="overflow-hidden rounded-lg bg-white p-8 text-center"
      >
        <div class="mx-auto mb-5 flex h-20 w-20 items-center justify-center rounded-full bg-red-50 text-5xl">
          ⏰
        </div>
        <h3 class="mb-1 text-xl font-bold text-gray-900">{{ $t('widget.quiz.time_expired.title') }}</h3>
        <p class="mb-7 text-sm text-gray-500">{{ $t('widget.quiz.time_expired.description') }}</p>
        <Button variant="blue" class="w-full" @click="handleRetry">
          {{ $t('widget.quiz.results.retry') }}
        </Button>
      </div>

      <WidgetQuizResults
        v-else-if="screen === 'results' && result"
        :result="result"
        @retry="handleRetry"
        @done="emit('done')"
      />
    </template>
  </div>
</template>
